import { useState, useEffect, useMemo } from 'react';
import { useUpdateEffect } from 'react-use';
import { useFormik } from 'formik';
import { debounce, get } from 'lodash';

import { useFetching } from '@mssgme/hooks';

/**
 * @template {object} FormValues
 * @typedef {(values: FormValues, formik: ReturnType<typeof useFormik>) => void} OnChangeMethod
 * @typedef {(values: FormValues, helpers: FormikHelpers<FormValues>) => (void | Promise<*>)} SubmitMethod
 * @param {object} config Config
 * @param {Partial<FormValues>} config.initialValues Initial values
 * @param {OnChangeMethod} config.onChange Change listener
 * @param {SubmitMethod} config.onSubmit Submit listener
 * @param {string} [config.errorsPrefix] If validation errors, generated by submit listener, would be nested under `errorsPrefix` path
 * @param {boolean} [config.resetOnSuccess=false] Reset form on successful submit
 * @param {boolean} [config.autoSubmit=false] Auto-submit if the form is dirty, accounting for debounce
 * @param {number} [config.autoSubmitDebounce=400] Auto-submit debounce
 * @returns {*} Enhanced Formik context
 */
export default function useEnhancedFormik({
    initialValues,
    onChange,
    onSubmit,
    errorsPrefix,
    resetOnSuccess = false,
    autoSubmit = false,
    autoSubmitDebounce = 400,
    ...rest
}) {
    const [warnings, setWarnings] = useState({});
    const submit = useFetching(onSubmit);

    const formik = useFormik({
        initialValues,
        onSubmit: (...args) => submit.run(...args),
        ...rest,
    });

    const validationErrors = errorsPrefix ? get(submit.validationErrors, errorsPrefix) : submit.validationErrors;
    const shouldAutoSubmit = autoSubmit && formik.dirty;

    useEffect(() => {
        if (submit.fulfilled) {
            formik.setSubmitting(false);
            formik.setStatus('success');
            if (resetOnSuccess) formik.resetForm();
        }
    }, [submit.fulfilled]);

    useEffect(() => {
        if (submit.rejected) {
            // TODO: handle errors in sentry
            formik.setSubmitting(false);
            formik.setStatus('failure');
        }
    }, [submit.rejected]);

    useEffect(() => void formik.setErrors(validationErrors), [validationErrors]);

    // TODO: move into independent component
    useUpdateEffect(() => void (onChange && onChange(formik.values, formik)), [formik.values]);

    const debouncedSubmit = useMemo(() => debounce(formik.submitForm, autoSubmitDebounce), [
        autoSubmitDebounce,
        formik.submitForm,
    ]);

    useUpdateEffect(() => {
        if (shouldAutoSubmit) {
            void debouncedSubmit();
        }
    }, [debouncedSubmit, shouldAutoSubmit]);

    return { warnings, setWarnings, submit, errorsPrefix, ...formik };
}
